import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Gamepad2, BarChart3, Filter } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

export default function GenreDetailDialog({ open, onOpenChange, genre, platform }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !genre) return;
    setLoading(true);
    axios.get(`${API}/stats/genre-detail`, { params: { genre, platform: platform || "all" } })
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, genre, platform]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#0A0A0A] border-white/10 text-white max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="w-5 h-5 text-purple-400" />
            {genre}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            {data ? `${data.games?.length || 0} games · ${data.totalCount} achievements · ${data.totalScore.toLocaleString()} points` : "Loading..."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
        ) : data?.games?.length > 0 ? (
          <div className="space-y-1 mt-2" data-testid="genre-games-list">
            {data.games.map(g => (
              <Link
                key={g.id}
                to={`/games/${g.id}`}
                onClick={() => onOpenChange(false)}
                className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors"
                data-testid={`genre-game-${g.id}`}
              >
                {g.boxArtUrl ? (
                  <img src={g.boxArtUrl} alt={g.name} className="w-9 h-12 rounded object-cover bg-black/50 shrink-0" />
                ) : (
                  <div className="w-9 h-12 rounded bg-white/10 flex items-center justify-center shrink-0">
                    <Gamepad2 className="w-4 h-4 text-white/20" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium truncate">{g.name}</p>
                  <p className="text-white/40 text-[10px]">{g.platform} · {g.system}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-cyan-400 text-xs font-mono">
                    {g.unlockedCount}/{g.totalAchievements}
                  </p>
                  {g.earnedScore > 0 && (
                    <p className="text-yellow-400/70 text-[10px] font-mono">{g.earnedScore.toLocaleString()} pts</p>
                  )}
                </div>
              </Link>
            ))}
            {/* View in Achievements */}
            <div className="pt-3 border-t border-white/5">
              <Button
                variant="outline"
                size="sm"
                className="w-full bg-white/5 border-white/10 text-white hover:bg-white/10"
                data-testid="genre-view-achievements"
                onClick={() => {
                  const params = new URLSearchParams({ status: "unlocked" });
                  if (platform && platform !== "all") params.set("platform", platform);
                  onOpenChange(false);
                  navigate(`/achievements?${params.toString()}`);
                }}
              >
                <Filter className="w-3.5 h-3.5 mr-1.5" />
                View in Achievements
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-white/30 text-sm text-center py-8">No games found for this genre.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
